import { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { canFeature } from '@/lib/perms';
import { useMeetings } from '@/contexts/MeetingsContext';
import api from '@/lib/api';
import { formatDateDMY, agendaText } from '@/components/meetingsData';
import { Plus, Trash2, IndianRupee, AlertTriangle, CalendarDays, MapPin } from 'lucide-react';

/*
  Design Fee Tab — ADMIN ONLY. Persists to project.design_fee via
  PUT /projects/:id/design-fee. On-site meetings are read live from the shared
  MeetingsContext (location === 'on_site'), which is now backed by the real API,
  so meetings logged in-app or via the Telegram bot both count here.
*/

const inr = (n) => `₹ ${Number(n || 0).toLocaleString('en-IN')}`;
const genId = () => (crypto.randomUUID?.() || Math.random().toString(36).slice(2));

const PAYMENT_STATUS = [
  { value: 'paid', label: 'Paid' },
  { value: 'pending', label: 'Pending' },
  { value: 'partial', label: 'Partial' },
];

const seedPayments = () => ([
  { id: genId(), milestone: 'Initial Deposit', dueDate: '', amount: 0, status: 'pending', receivedDate: null },
]);

export default function DesignFeeTab({ project }) {
  const { user } = useAuth();
  const { meetings } = useMeetings();

  const proposalTotal = project?.linked_proposal?.total_amount || 0;
  const hasProposal = proposalTotal > 0;
  const saved = project?.design_fee || {};

  const [totalContractValue, setTotalContractValue] = useState(saved.totalContractValue ?? proposalTotal);
  const [payments, setPayments] = useState(saved.payments?.length ? saved.payments : seedPayments());
  const [siteVisitRate, setSiteVisitRate] = useState(saved.siteVisitRate ?? 0);
  const firstRender = useRef(true);

  // Persist (debounced) whenever design fee state changes — skip the initial mount.
  useEffect(() => {
    if (firstRender.current) { firstRender.current = false; return; }
    if (!canFeature(user, 'financials') || !project?.id) return;
    const t = setTimeout(() => {
      api.put(`/projects/${project.id}/design-fee`, { totalContractValue, payments, siteVisitRate }).catch(() => {});
    }, 700);
    return () => clearTimeout(t);
  }, [totalContractValue, payments, siteVisitRate, project?.id, user?.role]);

  if (!canFeature(user, 'financials')) return null;

  const savePayment = (id, patch) => setPayments(prev => prev.map(p => p.id === id ? { ...p, ...patch } : p));
  const deletePayment = (id) => setPayments(prev => prev.filter(p => p.id !== id));
  const addPayment = () => setPayments(prev => [...prev, { id: genId(), milestone: '', dueDate: '', amount: 0, status: 'pending', receivedDate: null }]);
  const saveSiteVisitRate = (rate) => setSiteVisitRate(Math.max(0, Number(rate) || 0));

  const updatePayment = (id, key, value) => {
    if (key === 'status') {
      const patch = { status: value };
      if (value !== 'paid') patch.receivedDate = null;
      savePayment(id, patch);
    } else if (key === 'amount') {
      savePayment(id, { amount: Math.max(0, Number(value) || 0) });
    } else {
      savePayment(id, { [key]: value });
    }
  };

  // ── Derived totals ──
  const totalReceived = payments.filter(p => p.status === 'paid').reduce((s, p) => s + (Number(p.amount) || 0), 0);
  const totalPending = payments.filter(p => p.status !== 'paid').reduce((s, p) => s + (Number(p.amount) || 0), 0);
  const scheduledTotal = payments.reduce((s, p) => s + (Number(p.amount) || 0), 0);
  const overScheduled = scheduledTotal > Number(totalContractValue || 0);

  const onSiteMeetings = meetings.filter(m => m.location === 'on_site');
  const rateSet = Number(siteVisitRate) > 0;
  const siteVisitTotal = rateSet ? onSiteMeetings.length * Number(siteVisitRate) : 0;

  const netOutstanding = totalPending + siteVisitTotal;

  return (
    <div className="space-y-5" data-testid="design-fee-tab">
      {/* ── Section A: Fee Schedule ── */}
      <div className="glass-card rounded-xl p-5">
        <div className="flex items-center gap-2 mb-3">
          <IndianRupee size={16} className="text-[#3A8C8C]" />
          <h4 className="section-heading text-sm font-semibold font-['Manrope']">Design Fee</h4>
        </div>

        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <span className="text-xs text-[#6B7280]">Total Contract Value {hasProposal && <span className="text-[10px]">(from Proposal)</span>}</span>
            {!hasProposal && (
              <p className="text-[11px] text-[#C67D5B] mt-0.5" data-testid="no-proposal-note">No approved proposal found. Enter contract value manually.</p>
            )}
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-sm text-[#6B7280]">₹</span>
            <input type="number" min="0" value={totalContractValue}
              onChange={e => setTotalContractValue(Math.max(0, Number(e.target.value) || 0))}
              className="w-36 h-9 px-2 text-sm text-right rounded-lg border border-[#D4D0C8] bg-white focus:outline-none focus:border-[#3A8C8C]"
              data-testid="contract-value-input" />
          </div>
        </div>

        <div className="border-t border-[#E8E6E1] my-4" />

        <div className="flex items-center justify-between mb-2">
          <h5 className="text-xs font-semibold text-[#2C2C2E] uppercase tracking-wider">Schedule of Payments</h5>
          <button onClick={addPayment} data-testid="add-payment-btn"
            className="flex items-center gap-1 text-xs text-[#3A8C8C] font-medium hover:underline">
            <Plus size={13} /> Add Entry
          </button>
        </div>

        <div className="overflow-x-auto rounded-lg border border-[#E8E6E1]">
          <table className="w-full text-sm min-w-[680px]">
            <thead>
              <tr className="bg-[#FAFAF8]">
                <th className="px-3 py-2 text-[10px] font-semibold text-[#6B7280] uppercase text-left w-8">#</th>
                <th className="px-3 py-2 text-[10px] font-semibold text-[#6B7280] uppercase text-left">Milestone / Description</th>
                <th className="px-2 py-2 text-[10px] font-semibold text-[#6B7280] uppercase text-left w-36">Due Date</th>
                <th className="px-2 py-2 text-[10px] font-semibold text-[#6B7280] uppercase text-right w-28">Amount</th>
                <th className="px-2 py-2 text-[10px] font-semibold text-[#6B7280] uppercase text-left w-44">Status</th>
                <th className="w-8" />
              </tr>
            </thead>
            <tbody>
              {payments.map((p, idx) => (
                <tr key={p.id} className="group border-t border-[#E8E6E1] hover:bg-[#FAFAF8]/50" data-testid={`payment-row-${idx}`}>
                  <td className="px-3 py-1.5 text-xs text-[#6B7280]">{idx + 1}</td>
                  <td className="px-1 py-1">
                    <input value={p.milestone} onChange={e => updatePayment(p.id, 'milestone', e.target.value)} placeholder="Milestone…"
                      className="w-full px-2 py-1 text-sm bg-transparent border border-transparent hover:border-[#D4D0C8] focus:border-[#3A8C8C] rounded-md outline-none"
                      data-testid={`payment-milestone-${idx}`} />
                  </td>
                  <td className="px-1 py-1">
                    <input type="date" value={p.dueDate || ''} onChange={e => updatePayment(p.id, 'dueDate', e.target.value)}
                      className="w-full px-2 py-1 text-xs bg-transparent border border-transparent hover:border-[#D4D0C8] focus:border-[#3A8C8C] rounded-md outline-none"
                      data-testid={`payment-due-${idx}`} />
                  </td>
                  <td className="px-1 py-1">
                    <input type="number" min="0" value={p.amount || ''} onChange={e => updatePayment(p.id, 'amount', e.target.value)}
                      className="w-full px-2 py-1 text-sm text-right bg-transparent border border-transparent hover:border-[#D4D0C8] focus:border-[#3A8C8C] rounded-md outline-none"
                      data-testid={`payment-amount-${idx}`} />
                  </td>
                  <td className="px-1 py-1">
                    <div className="flex flex-col gap-1">
                      <select value={p.status} onChange={e => updatePayment(p.id, 'status', e.target.value)}
                        className={`w-full px-1.5 py-1 text-xs rounded-md border bg-white outline-none focus:border-[#3A8C8C] ${
                          p.status === 'paid' ? 'text-[#2E7D5B] border-[#2E7D5B]/30' : p.status === 'partial' ? 'text-[#C67D5B] border-[#C67D5B]/30' : 'text-[#6B7280] border-[#D4D0C8]'
                        }`}
                        data-testid={`payment-status-${idx}`}>
                        {PAYMENT_STATUS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                      </select>
                      {p.status === 'paid' && (
                        <input type="date" value={p.receivedDate || ''} onChange={e => updatePayment(p.id, 'receivedDate', e.target.value)}
                          title="Date Received"
                          className="w-full px-1.5 py-1 text-[11px] rounded-md border border-[#D4D0C8] bg-white outline-none focus:border-[#3A8C8C]"
                          data-testid={`payment-received-${idx}`} />
                      )}
                    </div>
                  </td>
                  <td className="px-1 py-1 align-top">
                    <button onClick={() => deletePayment(p.id)} className="opacity-0 group-hover:opacity-100 p-1 text-[#C4727F] hover:bg-[#C4727F]/10 rounded transition-all" data-testid={`payment-delete-${idx}`}>
                      <Trash2 size={13} />
                    </button>
                  </td>
                </tr>
              ))}
              {payments.length === 0 && (
                <tr><td colSpan={6} className="px-3 py-4 text-center text-xs text-[#6B7280]">No payment entries. Click "Add Entry".</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {overScheduled && (
          <div className="flex items-center gap-1.5 mt-2 text-[11px] text-[#C67D5B]" data-testid="over-scheduled-warning">
            <AlertTriangle size={12} /> Scheduled payments ({inr(scheduledTotal)}) exceed the total contract value ({inr(totalContractValue)}).
          </div>
        )}

        <div className="border-t border-[#E8E6E1] my-3" />
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-end gap-x-8 gap-y-1 text-sm">
          <div className="flex items-center justify-between sm:justify-start gap-3">
            <span className="text-[#6B7280]">Total Received:</span>
            <span className="font-semibold text-[#2E7D5B]" data-testid="total-received">{inr(totalReceived)}</span>
          </div>
          <div className="flex items-center justify-between sm:justify-start gap-3">
            <span className="text-[#6B7280]">Total Pending:</span>
            <span className="font-semibold text-[#C67D5B]" data-testid="total-pending">{inr(totalPending)}</span>
          </div>
        </div>
      </div>

      {/* ── Section B: Site Visit Charges ── */}
      <div className="glass-card rounded-xl p-5">
        <div className="flex items-center gap-2 mb-3">
          <MapPin size={16} className="text-[#3A8C8C]" />
          <h4 className="section-heading text-sm font-semibold font-['Manrope']">Site Visit Charges</h4>
        </div>

        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
          <span className="text-xs text-[#6B7280]">Rate per Site Visit</span>
          <div className="flex items-center gap-1.5">
            <span className="text-sm text-[#6B7280]">₹</span>
            <input type="number" min="0" value={siteVisitRate}
              onChange={e => saveSiteVisitRate(e.target.value)}
              className="w-28 h-9 px-2 text-sm text-right rounded-lg border border-[#D4D0C8] bg-white focus:outline-none focus:border-[#3A8C8C]"
              data-testid="site-visit-rate-input" />
          </div>
        </div>

        <p className="text-xs text-[#6B7280] mb-2">On-Site Meetings <span className="text-[10px]">(from Meetings tab)</span></p>

        {!rateSet ? (
          <div className="text-center py-6 border border-dashed border-[#D4D0C8] rounded-lg" data-testid="site-visit-no-rate">
            <p className="text-sm text-[#6B7280]">Set a rate above to calculate charges</p>
          </div>
        ) : onSiteMeetings.length === 0 ? (
          <div className="text-center py-6 border border-dashed border-[#D4D0C8] rounded-lg">
            <p className="text-sm text-[#6B7280]">No on-site meetings recorded in the Meetings tab.</p>
          </div>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-[#E8E6E1]">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#FAFAF8]">
                  <th className="px-3 py-2 text-[10px] font-semibold text-[#6B7280] uppercase text-left w-8">#</th>
                  <th className="px-3 py-2 text-[10px] font-semibold text-[#6B7280] uppercase text-left w-32">Date</th>
                  <th className="px-3 py-2 text-[10px] font-semibold text-[#6B7280] uppercase text-left">Agenda</th>
                  <th className="px-3 py-2 text-[10px] font-semibold text-[#6B7280] uppercase text-right w-28">Charge</th>
                </tr>
              </thead>
              <tbody>
                {onSiteMeetings.map((m, idx) => (
                  <tr key={m.id} className="border-t border-[#E8E6E1]" data-testid={`site-visit-row-${idx}`}>
                    <td className="px-3 py-2 text-xs text-[#6B7280]">{idx + 1}</td>
                    <td className="px-3 py-2 text-xs text-[#2C2C2E] flex items-center gap-1">
                      <CalendarDays size={11} strokeWidth={1.5} className="text-[#6B7280]" /> {formatDateDMY(m.date)}
                    </td>
                    <td className="px-3 py-2 text-xs text-[#2C2C2E]">{agendaText(m)}</td>
                    <td className="px-3 py-2 text-sm text-right text-[#2C2C2E]">{inr(siteVisitRate)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-[#FAFAF8]">
                  <td colSpan={3} className="px-3 py-2.5 text-xs font-semibold text-[#2C2C2E] text-right border-t border-[#D4D0C8] uppercase tracking-wider">Total Site Visit Charges</td>
                  <td className="px-3 py-2.5 text-sm font-bold text-[#3A8C8C] text-right border-t border-[#D4D0C8]" data-testid="site-visit-total">{inr(siteVisitTotal)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>

      {/* ── Section C: Grand Total Summary ── */}
      <div className="bg-white rounded-xl border border-[#E8E6E1] border-l-4 border-l-[#3A8C8C] p-5" data-testid="design-fee-summary">
        <h4 className="section-heading text-sm font-semibold font-['Manrope'] mb-3">Summary</h4>
        <div className="space-y-2 text-sm">
          <Row label="Total Contract Value" value={inr(totalContractValue)} />
          <Row label="Total Received" value={inr(totalReceived)} valueClass="text-[#2E7D5B]" />
          <Row label="Total Pending" value={inr(totalPending)} valueClass="text-[#C67D5B]" />
          <Row label="Total Site Visit Charges" value={inr(siteVisitTotal)} />
          <div className="border-t border-[#E8E6E1] my-1" />
          <div className="flex items-center justify-between">
            <div>
              <span className="text-sm font-medium text-[#2C2C2E]">Net Outstanding</span>
              <span className="block text-[10px] text-[#6B7280]">(Pending + Site Visit Charges)</span>
            </div>
            <span className="text-lg font-bold text-[#3A8C8C]" data-testid="net-outstanding">{inr(netOutstanding)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, valueClass = 'text-[#2C2C2E]' }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-[#6B7280]">{label}</span>
      <span className={`font-semibold ${valueClass}`}>{value}</span>
    </div>
  );
}
